/* eslint-disable max-lines */
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { applyTrackingId } from '../../../AnalyticsContext/applyTrackingId';
import { useEscapeKeyHandler } from '../../../hooks/keyboard/useEscapeKeyHandler';
import { useChangeEvent } from '../../../hooks/useChangeEvent';
import { generateSuggestionData } from '../LocationSearch/GenerateSuggestionData';
import { LocationSearchBar } from '../LocationSearch/LocationSearchBar';
import { NewSuggestions } from '../Suggestions/NewSuggestions/NewSuggestions';
import { SuggestionData } from '../Suggestions/types';
import { useSearchFilters } from '../filters/useSearchFilters';
import { FilterChoice } from '../types/FilterChoice';
import { LocationChoice, renderLocationChoice } from '../types/LocationChoice';
import { useSuggestionHandler } from '../useSuggestionHandler';
import { renderNewQuery } from './NewSearchBar';
import {
  Bar,
  BarLocation,
  SpecialtyIcon,
  InputSpecialty,
  SearchBarView,
  LocationMarkerIcon,
  SpecialtyPlaceholder,
  LocationPlaceholder,
  ClearIcon,
} from './NewSearchBar.styles';

const DEFAULT_PLACEHOLDER_SPECIALTY = 'Service, practice or practitioner';

export interface MobileProps {
  className?: string;
  specialtyFocused?: boolean;
  locationFocused?: boolean;
  specialtyValue: string;
  locationValue?: string;
  setSpecialtyFocused?: Setter<boolean>;
  setLocationFocused?: Setter<boolean>;
  onSpecialtyTextChange?: Setter<string>;
  isSearchMultiLine?: boolean;
  setIsSearchMultiLine?: Setter<boolean>;
  inputRef?: React.RefObject<HTMLInputElement>;
  placeholder?: string;
  setLocationValue: Setter<string>;
  searchResultsSingleViewIsShowing: boolean;
  setSearchResultsSingleViewIsShowing: Setter<boolean>;
  showPlaceholder: boolean;
  setShowPlaceholder: Setter<boolean>;
}

export const SearchBarV2Mobile = ({
  className,
  setSpecialtyFocused,
  setLocationFocused,
  setIsSearchMultiLine,
  isSearchMultiLine = false,
  specialtyFocused = false,
  locationFocused = false,
  placeholder,
  searchResultsSingleViewIsShowing,
  setSearchResultsSingleViewIsShowing,
  showPlaceholder,
  setShowPlaceholder,
}: MobileProps) => {
  const ref = useRef<HTMLInputElement>(null);

  const runSearchFromSuggestion = useSuggestionHandler();
  const [filters] = useSearchFilters();

  const [locationValue, setLocationValue] = useState<LocationChoice | undefined>(filters.location);

  const [locationDisplayValue, setLocationDisplayValue] = useState(
    filters.location ? renderLocationChoice(filters.location) : '',
  );

  const [specialtyValue, setSpecialtyValue] = useState<FilterChoice | undefined>(filters);

  const [specialtyDisplayValue, setSpecialtyDisplayValue] = useState(renderNewQuery(filters) ?? '');

  const [showLocationClearIcon, setShowLocationClearIcon] = useState(locationDisplayValue !== '');

  /** When the search bar transitions to 'focused', the field should acquire a cursor, and vice-versa */
  useEffect(() => {
    if (specialtyFocused) {
      ref.current?.focus();
    } else {
      ref.current?.blur();
    }
  }, [specialtyFocused, ref]);

  const observedPlaceholder = placeholder ?? DEFAULT_PLACEHOLDER_SPECIALTY;

  useEscapeKeyHandler(() => setSpecialtyFocused?.(false));

  const handleSpecialtyInputKeyDown = useCallback(() => {
    if (!specialtyFocused) {
      setSpecialtyFocused?.(true);
    }

    setSearchResultsSingleViewIsShowing(false);
  }, [specialtyFocused, setSpecialtyFocused, setSearchResultsSingleViewIsShowing]);

  const handleLocationInputKeyDown = useCallback(() => {
    if (!locationFocused) {
      setLocationFocused?.(true);
    }
  }, [locationFocused, setLocationFocused]);

  const handleSpecialtyChange = useChangeEvent(setSpecialtyDisplayValue);

  const handleSpecialtyFocus = useCallback(() => {
    if (isSearchMultiLine) {
      setIsSearchMultiLine?.(true);
      setSpecialtyFocused?.(true);
    }

    setShowPlaceholder(false);
  }, [setSpecialtyFocused, isSearchMultiLine, setIsSearchMultiLine, setShowPlaceholder]);

  const handleLocationFocus = useCallback(() => {
    if (isSearchMultiLine) {
      setIsSearchMultiLine?.(true);
      setLocationFocused?.(true);
      setLocationDisplayValue('');
      setShowLocationClearIcon(false);
    }

    setShowPlaceholder(false);
  }, [
    setLocationFocused,
    isSearchMultiLine,
    setIsSearchMultiLine,
    setShowPlaceholder,
    setLocationDisplayValue,
  ]);

  const handleSpecialtyPlaceholderClick = () => {
    if (isSearchMultiLine) {
      setSpecialtyFocused?.(true);
      setSpecialtyDisplayValue?.('');
    } else {
      setIsSearchMultiLine?.(true);
    }

    isSearchMultiLine ? setShowPlaceholder(false) : setShowPlaceholder(true);
  };

  const handleLocationPlaceholderClick = () => {
    if (isSearchMultiLine) {
      setLocationFocused?.(true);
    } else {
      setIsSearchMultiLine?.(true);
    }

    isSearchMultiLine ? setShowPlaceholder(false) : setShowPlaceholder(true);
  };

  const handleOnLocationSelect = (location: LocationChoice) => {
    setLocationFocused?.(false);
    setLocationDisplayValue(renderLocationChoice(location));
    setIsSearchMultiLine?.(false);
    setLocationValue(location);
    onSearchUpdate(undefined, location);
  };

  const setText = useCallback(
    (text: string) => {
      setSpecialtyDisplayValue?.(text);
      ref?.current?.focus();
    },
    [setSpecialtyDisplayValue, ref],
  );

  const handleClearText = (event: MouseEvent<SVGElement>) => {
    setSpecialtyDisplayValue?.('');
    event?.preventDefault();
  };

  const handleClearLocation = (event: MouseEvent<SVGElement>) => {
    setLocationDisplayValue?.('');
    event?.preventDefault();
    event.stopPropagation();
  };

  const onSearchUpdate = (spec?: FilterChoice, loc?: LocationChoice) => {
    const suggestion = spec ?? specialtyValue;
    const location = loc ?? locationValue;

    const sugg = generateSuggestionData(
      { ...suggestion, onlineAppointments: filters.onlineAppointments ?? {} },
      location,
    );

    if (!location) {
      setSpecialtyFocused?.(false);
      setShowPlaceholder?.(false);
      setIsSearchMultiLine?.(true);
      setLocationFocused?.(true);
    }

    if (!suggestion || Object.keys(suggestion).length === 0) {
      setLocationFocused?.(false);
      setShowPlaceholder?.(false);
      setIsSearchMultiLine?.(true);
      setSpecialtyFocused?.(true);
    }

    if (!!suggestion && !!location) {
      runSearchFromSuggestion(sugg);
      setLocationFocused?.(false);
      setSpecialtyFocused?.(false);
      setShowPlaceholder(true);
      setIsSearchMultiLine?.(false);
    }
  };

  const localHandleOnSpecialtySuggestionCallback = (selection: SuggestionData) => {
    if (selection.type === 'PracticeSuggestion' || selection.type === 'PractitionerSuggestion') {
      setLocationFocused?.(false);
      setSpecialtyFocused?.(false);
      setShowPlaceholder?.(true);
      setIsSearchMultiLine?.(false);

      return;
    }

    setSpecialtyValue(selection.type === 'FilterSuggestion' ? selection.filters : undefined);
    onSearchUpdate(selection.type === 'FilterSuggestion' ? selection.filters : undefined);
  };

  return (
    <SearchBarView
      className={className}
      isSearchMultiLine={isSearchMultiLine}
      isSearchInstructionVisible={isSearchMultiLine && !specialtyFocused && !locationFocused}
      {...applyTrackingId(isSearchMultiLine ? undefined : 'Header Search Bar')}
    >
      {isSearchMultiLine && locationFocused ? null : (
        <Bar isSearchMultiLine={isSearchMultiLine}>
          <SpecialtyIcon isSearchMultiLine={isSearchMultiLine} />
          {showPlaceholder ? (
            <SpecialtyPlaceholder
              onClick={handleSpecialtyPlaceholderClick}
              isSearchMultiLine={isSearchMultiLine}
            >
              {specialtyDisplayValue !== '' ? specialtyDisplayValue : observedPlaceholder}
            </SpecialtyPlaceholder>
          ) : (
            <>
              <InputSpecialty
                ref={ref}
                onChange={handleSpecialtyChange}
                placeholder={observedPlaceholder}
                onFocus={handleSpecialtyFocus}
                onBlur={() => {
                  if (!isSearchMultiLine && specialtyFocused) {
                    setSpecialtyFocused?.(false);
                  }
                }}
                value={specialtyDisplayValue}
                onKeyDown={handleSpecialtyInputKeyDown}
                data-testid="searchBarInput"
                data-link-id="Search bar specialty selection"
              />
              {specialtyDisplayValue !== '' && <ClearIcon onMouseDown={handleClearText} />}
            </>
          )}
        </Bar>
      )}
      {isSearchMultiLine && specialtyFocused ? null : (
        <BarLocation
          isSearchMultiLine={isSearchMultiLine}
          isBorderTopVisible={isSearchMultiLine && !specialtyFocused && !locationFocused}
        >
          <LocationMarkerIcon isSearchMultiLine={isSearchMultiLine} />

          {showPlaceholder ? (
            <LocationPlaceholder
              onClick={handleLocationPlaceholderClick}
              isSearchMultiLine={isSearchMultiLine}
              $noLocation={locationDisplayValue === ''}
            >
              {locationDisplayValue !== '' ? locationDisplayValue : 'Suburb, postcode, or region'}
            </LocationPlaceholder>
          ) : (
            <>
              <LocationSearchBar
                locationFocused={locationFocused}
                onFocus={handleLocationFocus}
                value={locationDisplayValue ?? 'Suburb, postcode, or region'}
                setValue={setLocationDisplayValue}
                onLocationSelected={handleOnLocationSelect}
                onKeyDown={handleLocationInputKeyDown}
                onLocationTextChange={value => setShowLocationClearIcon(value !== '')}
                setShowPlaceholder={setShowPlaceholder}
              />
              {showLocationClearIcon && <ClearIcon onMouseDown={handleClearLocation} />}
            </>
          )}
        </BarLocation>
      )}
      {specialtyFocused && (
        <NewSuggestions
          setValue={setText}
          searchTerm={specialtyDisplayValue}
          handleOnSpecialtySuggestionCallback={localHandleOnSpecialtySuggestionCallback}
          enabledFilterOffset={false}
          searchResultsSingleViewIsShowing={searchResultsSingleViewIsShowing}
          setSearchResultsSingleViewIsShowing={setSearchResultsSingleViewIsShowing}
          setLocationFocused={setLocationFocused}
          locationValue={locationDisplayValue}
          setSpecialtyFocused={setSpecialtyFocused}
          setShowPlaceholder={setShowPlaceholder}
        />
      )}
    </SearchBarView>
  );
};
